import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import type { EChartsOption } from 'echarts';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([BarChart, LineChart, PieChart, CanvasRenderer, GridComponent, TooltipComponent]);

/**
 * Semantic color variants for the KPI tile, mapped to the M3 theme tokens.
 */
export type DtisKpiTileColor = 'primary' | 'success' | 'warning' | 'error' | 'information' | 'neutral';

/**
 * Direction of the trend indicator shown next to the tile label.
 */
export type DtisKpiTileTrend = 'up' | 'down' | 'neutral';

const COLOR_CLASSES: Record<DtisKpiTileColor, { icon: string; iconBg: string }> = {
  primary: { icon: 'text-primary', iconBg: 'bg-primary/10' },
  success: { icon: 'text-onSuccessContainer', iconBg: 'bg-successContainer' },
  warning: { icon: 'text-onWarningContainer', iconBg: 'bg-warningContainer' },
  error: { icon: 'text-error', iconBg: 'bg-error/10' },
  information: {
    icon: 'text-onInformationContainer',
    iconBg: 'bg-informationContainer',
  },
  neutral: { icon: 'text-onSurfaceVariant', iconBg: 'bg-outlineVariant/40' },
};

const TREND_CLASSES: Record<DtisKpiTileTrend, string> = {
  up: 'text-primary',
  down: 'text-error',
  neutral: 'text-onSurfaceVariant',
};

const TREND_ICONS: Record<DtisKpiTileTrend, string> = {
  up: 'trending_up',
  down: 'trending_down',
  neutral: 'trending_flat',
};

/**
 * Reusable KPI tile used on dashboard-style pages to highlight a single metric.
 *
 * Supports an optional embedded ECharts chart (e.g. a sparkline, donut or bar
 * chart) passed in via `chartOptions`.
 *
 * @example
 * ```html
 * <dtis-kpi-tile
 *   label="Total Instances"
 *   [value]="42"
 *   icon="dns"
 *   color="primary"
 * />
 * ```
 */
@Component({
  selector: 'dtis-kpi-tile',
  imports: [CommonModule, MatIconModule, NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  template: `    
    <div
      class="border-outlineVariant bg-surface flex h-full w-full flex-col gap-3 rounded-xl border p-4"
    >
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          @if (icon()) {
          <span
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            [ngClass]="colorClasses().iconBg"
          >
            <mat-icon
              class="h-5! w-5! text-[20px]!"
              [ngClass]="colorClasses().icon"
              >{{ icon() }}</mat-icon
            >
          </span>
          }
          <span class="text-title-small text-onSurfaceVariant uppercase">{{
            label()
          }}</span>
        </div>
        @if (trend()) {
        <span
          class="text-label-small flex shrink-0 items-center gap-0.5"
          [ngClass]="trendClasses()"
        >
          <mat-icon class="h-4! w-4! text-[16px]!">{{ trendIcon() }}</mat-icon>
          {{ trendLabel() }}
        </span>
        }
      </div>

      <div class="flex items-baseline gap-1">
        @if (loading()) {
        <span class="text-title-large text-onSurfaceVariant animate-pulse"
          >&hellip;</span
        >
        } @else {
        <span class="text-headline-large text-onSurfaceVariant">{{ value() }}</span>
        @if (unit()) {
        <span class="text-headline-medium text-onSurfaceVariant">{{ unit() }}</span>
        } }
      </div>

      @if (description()) {
      <p class="text-body-small text-onSurfaceVariant">{{ description() }}</p>
      } @if (chartOptions() && !loading()) {
      <div
        echarts
        [options]="chartOptions()!"
        [style.height]="chartHeight()"
        class="w-full"
      ></div>
      }
    </div>
  `,
})
export class DtisKpiTileComponent {
  /**
   * The KPI label/title shown at the top of the tile.
   */
  label = input.required<string>();
  /**
   * The primary value to highlight, e.g. a count, percentage or formatted number.
   */
  value = input.required<string | number>();
  /**
   * An optional unit or suffix rendered next to the value (e.g. "%", "ms").
   */
  unit = input<string>('');
  /**
   * An optional Material icon name shown next to the label.
   */
  icon = input<string>('');
  /**
   * An optional short description rendered below the value.
   */
  description = input<string>('');
  /**
   * Semantic color variant for the icon background and accents.
   */
  color = input<DtisKpiTileColor>('primary');
  /**
   * Optional trend direction shown as a small badge next to the label.
   */
  trend = input<DtisKpiTileTrend | undefined>(undefined);
  /**
   * Label rendered next to the trend icon (e.g. "+12% vs last week").
   */
  trendLabel = input<string>('');
  /**
   * Optional ECharts options rendered at the bottom of the tile (e.g. sparkline,
   * donut or bar chart).
   */
  chartOptions = input<EChartsOption | undefined>(undefined);
  /**
   * Height of the embedded chart, e.g. "64px".
   */
  chartHeight = input<string>('64px');
  /**
   * When true, shows a loading placeholder instead of the value and chart.
   */
  loading = input<boolean>(false);

  /**
   * Tailwind classes for the current color variant.
   */
  colorClasses = computed(() => COLOR_CLASSES[this.color()]);
  /**
   * Tailwind classes for the current trend direction.
   */
  trendClasses = computed(() => {
    const trend = this.trend();
    return trend ? TREND_CLASSES[trend] : '';
  });
  /**
   * Material icon name for the current trend direction.
   */
  trendIcon = computed(() => {
    const trend = this.trend();
    return trend ? TREND_ICONS[trend] : '';
  });
}
